// ============================================================================
// src/lib/cattleOperationsApi.ts — the one client of public.cattle_batch_operation
// ----------------------------------------------------------------------------
// Migration 240 (R4) replaces the direct client writes on the four cattle
// tables with ONE audited server entrypoint: a narrow change under the batch
// row lock, expected-before compare-and-set, the Activity row and an immutable
// request receipt in the same transaction. Design: wcf-receipts\cc4-r4-cattle\DESIGN-v3.md.
//
// WHAT THIS MODULE OWNS: the transport and the meaning of every reply.
//
//   * REQUEST IDS. One per user action (`newCattleRequestId`). A retry of the
//     same action reuses it: the server either replays the stored result or
//     applies the operation once. A new id is only ever taken after a
//     CONFIRMED outcome.
//   * RETRIES. A transport failure (no reply, timeout) is retried here with
//     the SAME id — safe because of the receipt. A database refusal (any
//     SQLSTATE) is never retried: it is an answer, not a failure.
//   * REPLAYS. A replayed result carries no rows (the receipt deliberately
//     holds no row copy). `mustReread` tells the caller to refetch before it
//     adopts anything; so does `unknown`.
//
// WHAT IT DOES NOT OWN: what the UI shows. Callers map outcomes to notices.
// ============================================================================

import type {SupabaseClient} from './supabaseTypes.js';
import {newClientSubmissionId} from './clientSubmissionId.js';

export type CattleOperation =
  | 'batch.create'
  | 'batch.update'
  | 'batch.weight'
  | 'batch.complete'
  | 'batch.reopen'
  | 'forecast_settings.save'
  | 'heifer.apply'
  | 'hidden.set'
  // R-H 245 (migration 245): the cattle animal-level family, served by
  // public.cattle_animal_operation — same envelope, same receipts-by-request-id
  // contract, its own receipts table. See wcf-receipts\rh-cattle\DESIGN-v1.md.
  | 'animal.create'
  | 'animal.patch'
  | 'animal.retag'
  | 'calving.create'
  | 'import.cattle'
  | 'import.calving';

export type CattleOpTarget = {
  batch_id?: string;
  cattle_id?: string;
  month_key?: string;
  // 245's families: animal.create/import.cattle carry the caller's stream tag
  // in target.source; the calving family targets the dam row.
  source?: string;
  dam_cattle_id?: string;
};

type Json = unknown;

// 245's animal-level family talks to its own SECURITY DEFINER entrypoint; the
// batch/forecast family stays on 240's. Everything else about the transport —
// the request-id receipt replay, the retry rule, the reply reader — is shared.
function rpcNameFor(operation: CattleOperation): string {
  return operation === 'animal.create' ||
    operation === 'animal.patch' ||
    operation === 'animal.retag' ||
    operation === 'calving.create' ||
    operation === 'import.cattle' ||
    operation === 'import.calving'
    ? 'cattle_animal_operation'
    : 'cattle_batch_operation';
}

export type CattleConflict = {field: string; expected: Json; current: Json};

export type CattleOpRequest = {
  requestId: string;
  operation: CattleOperation;
  target: CattleOpTarget;
  expected?: Record<string, Json>;
  change?: Record<string, Json>;
};

export type CattleOpOutcome =
  | {
      status: 'saved';
      kind: 'created' | 'updated' | 'deleted';
      written: Record<string, Json>;
      revision: string | null;
      eventIds: string[];
      rows: Record<string, Json> | null;
      replayed: boolean;
      mustReread: boolean;
    }
  | {status: 'no_change'; rows: Record<string, Json> | null}
  | {status: 'conflict'; conflicts: CattleConflict[]; rows: Record<string, Json> | null}
  | {status: 'not_attached'; rows: Record<string, Json> | null}
  | {status: 'rejected'; code: string; message: string}
  | {status: 'unknown'; message: string; mustReread: true};

/** A fresh id for ONE user action. Keep it with the draft and reuse it on retry. */
export function newCattleRequestId(): string {
  return 'cattle-' + newClientSubmissionId();
}

const ATTEMPTS = 3;
const ATTEMPT_TIMEOUT_MS = 8000;
// A five-character SQLSTATE (22023, P0002, WCF01, ...) is the database's answer.
const SQLSTATE = /^[0-9A-Z]{5}$/;

type RpcError = {code?: string; message?: string} | null;

function rowsOf(data: Record<string, Json>): Record<string, Json> | null {
  const rows = data.rows;
  return rows && typeof rows === 'object' && !Array.isArray(rows) ? (rows as Record<string, Json>) : null;
}

/** Read one reply. Anything that does not answer THIS request is not a receipt. */
export function interpretCattleReply(request: CattleOpRequest, data: unknown): CattleOpOutcome {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, Json>;
  // 245:716 builds `v_receipt := jsonb_build_object('ok', true, ...)` without
  // `v_base ||`, and 245:391's replay returns that stored receipt without the
  // envelope (245's conflict replies do carry `v_base ||`). Migration 251
  // re-issues `cattle_animal_operation` with the envelope on both paths.
  const isUnenvelopedAnimalReply =
    rpcNameFor(request.operation) === 'cattle_animal_operation' && !('request_id' in d) && !('operation' in d);
  if (!isUnenvelopedAnimalReply && (d.request_id !== request.requestId || d.operation !== request.operation)) {
    return {status: 'unknown', message: 'Could not confirm this change was saved. Reload to check.', mustReread: true};
  }
  const rows = rowsOf(d);
  switch (d.kind) {
    case 'created':
    case 'updated':
    case 'deleted': {
      if (d.ok !== true) break;
      const replayed = d.replayed === true;
      return {
        status: 'saved',
        kind: d.kind,
        written: (d.written && typeof d.written === 'object' ? d.written : {}) as Record<string, Json>,
        revision: typeof d.revision === 'string' ? d.revision : null,
        eventIds: Array.isArray(d.event_ids) ? (d.event_ids as string[]) : [],
        rows: replayed ? null : rows,
        replayed,
        mustReread: replayed || rows === null,
      };
    }
    case 'no_change':
      return {status: 'no_change', rows};
    case 'conflict':
      return {status: 'conflict', conflicts: Array.isArray(d.conflicts) ? (d.conflicts as CattleConflict[]) : [], rows};
    case 'not_attached':
      return {status: 'not_attached', rows};
  }
  return {status: 'unknown', message: 'Could not confirm this change was saved. Reload to check.', mustReread: true};
}

/**
 * Apply one cattle operation. Never throws. Transport failures are retried with
 * the same request id; a database refusal comes back as `rejected` at once.
 */
export async function applyCattleOperation(sb: SupabaseClient, request: CattleOpRequest): Promise<CattleOpOutcome> {
  let lastMessage = 'No reply from the server.';
  for (let attempt = 1; attempt <= ATTEMPTS; attempt++) {
    let timer: ReturnType<typeof setTimeout> | null = null;
    try {
      // Literal RPC names: the render-read-site inventory pins every server
      // call by its literal entrypoint (M1, REVIEW-245-v1).
      const rpcArgs = {
        p_request_id: request.requestId,
        p_operation: request.operation,
        p_target: request.target,
        p_expected: request.expected ?? {},
        p_change: request.change ?? {},
      };
      const call =
        rpcNameFor(request.operation) === 'cattle_animal_operation'
          ? sb.rpc('cattle_animal_operation', rpcArgs)
          : sb.rpc('cattle_batch_operation', rpcArgs);
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ATTEMPT_TIMEOUT_MS);
      });
      const {data, error} = (await Promise.race([call, timeout])) as {data: unknown; error: RpcError};
      if (error) {
        if (error.code && SQLSTATE.test(error.code)) {
          return {status: 'rejected', code: error.code, message: error.message || 'The change was refused.'};
        }
        if (error.code) {
          // L1 (REVIEW-244-v1): a coded error that is not a SQLSTATE is a
          // PostgREST answer (PGRST...). It is never retried — only a code-less
          // transport loss (status 0 / gateway 5xx) is.
          return {status: 'rejected', code: error.code, message: error.message || 'The change was refused.'};
        }
        throw new Error(error.message || 'transport');
      }
      return interpretCattleReply(request, data);
    } catch (e) {
      lastMessage = (e as {message?: string}).message || lastMessage;
      if (attempt < ATTEMPTS) await new Promise((r) => setTimeout(r, 1000 * attempt));
    } finally {
      if (timer !== null) clearTimeout(timer);
    }
  }
  console.warn('cattle operation: no confirmed reply after retries:', lastMessage);
  return {status: 'unknown', message: 'Could not reach the server. Your change is kept; retry.', mustReread: true};
}

/** The user-facing sentence for a refusal, by SQLSTATE (the pig contract's shape).
 * `family` rewords the row-existence sentences for the 245 animal family
 * (an id conflict there is a saved animal, not a batch name). */
export function cattleRejectionMessage(
  outcome: Extract<CattleOpOutcome, {status: 'rejected'}>,
  family: 'batch' | 'animal' = 'batch',
): string {
  switch (outcome.code) {
    case '22023':
      return outcome.message.replace(/^(cattle_batch_operation|cattle_animal_operation): /, '');
    case '23505':
      return family === 'animal'
        ? 'This animal was already saved. Reload the cattle list to check.'
        : 'This was already saved, or another batch has that name. Reload to check.';
    case 'P0002':
      return family === 'animal'
        ? 'This animal was removed elsewhere. Reload and try again.'
        : 'This batch was removed elsewhere. Reload to see the current list.';
    case '42501':
      return 'Your session or role changed. Sign in again; your change is kept.';
    case 'WCF01':
      return 'This change no longer matches what was saved. Reload and try again.';
    default:
      return outcome.message || 'The change was refused.';
  }
}

/**
 * The error MESSAGE a caller that treats the operation as a plain promise
 * should see: saved/no_change resolve, everything else throws with the notice
 * text the surface already renders. The catalog's cattle.batch.patch entry
 * uses this so its invalidation set stays the one place refresh is declared.
 */
export class CattleOperationError extends Error {
  readonly outcome: CattleOpOutcome;
  constructor(outcome: CattleOpOutcome, message: string) {
    super(message);
    this.outcome = outcome;
  }
}

/** Run one operation, resolving on saved/no_change and throwing otherwise. */
export async function runCattleOperation(sb: SupabaseClient, request: CattleOpRequest): Promise<CattleOpOutcome> {
  const outcome = await applyCattleOperation(sb, request);
  switch (outcome.status) {
    case 'saved':
    case 'no_change':
      return outcome;
    case 'conflict':
      throw new CattleOperationError(outcome, 'Changed by someone else since you loaded — reload and try again.');
    case 'not_attached':
      throw new CattleOperationError(
        outcome,
        'That cow is no longer attached to this batch. Reload to see the current roster.',
      );
    case 'rejected':
      throw new CattleOperationError(outcome, cattleRejectionMessage(outcome));
    case 'unknown':
      throw new CattleOperationError(outcome, outcome.message);
  }
}
